import React, { memo, useMemo, lazy, Suspense, useState } from 'react'
import { Loading } from 'components/shared'
import LogoSplash from 'components/shared/LogoSplash'
import { useDispatch, useSelector } from 'react-redux'
import {
	LAYOUT_TYPE_MODERN,
} from 'constants/theme.constant'
import useAuth from 'utils/hooks/useAuth'
import useDirection from 'utils/hooks/useDirection'
import useLocale from 'utils/hooks/useLocale'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getTicketGlobalData } from 'views/tickets/store/globalSlice'
import TicketDialog from 'views/tickets/ticketList/TicketDialog'
import { useQuery } from '@apollo/client'
import { PARAMETERS_TENANT } from 'services/graphql/api/Parameters'
import { setParameters, setPublicParameters } from 'store/auth/userSlice'
import moment from 'moment'
import 'moment-timezone';
import { apiGetParametersSettings, apiGetPublicParameters } from 'services/ParametersService'
import { updateDynamicTranslations } from 'locales'
import NotificationManager from 'components/shared/NotificationManager'
import PusherDebug from 'components/shared/PusherDebug'

const layouts = {
	[LAYOUT_TYPE_MODERN]: lazy(() => import('./ModernLayout')),
}

const Layout = () => {
	// Estado para controlar si las traducciones están cargadas
	const [translationsLoaded, setTranslationsLoaded] = useState(false)

	const layoutType = useSelector((state) => state.theme.layout.type)
	const { authenticated } = useAuth()
	const { session, user } = useSelector((state) => state.auth)
	const { signedIn } = session
	const { timezone } = user

	useDirection()
	useLocale()

	const dispatch = useDispatch()
	const navigate = useNavigate()

	useEffect(() => {
		if (authenticated) setTimeout(() => fetchData(), 500)
	}, [authenticated])

	useEffect(() => {
		if (timezone?.code) {
			moment.tz.setDefault(timezone?.code);
		}
	}, [timezone])

	// Listener para mensajes del Service Worker (notificaciones push + keep-alive)
	useEffect(() => {
		const handleServiceWorkerMessage = (event) => {
			// Navegación a ticket
			if (event.data && event.data.type === 'NAVIGATE_TO_TICKET') {
				const { ticketId, url } = event.data
				console.log('🎯 [Layout] Navegando a ticket:', ticketId, url)

				if (url) {
					navigate(url)
				}
			}
			// Keep-alive ping del Service Worker
			else if (event.data && event.data.type === 'SW_KEEP_ALIVE_PING') {
				const { source, timestamp } = event.data
				console.log(`💚 [Layout] Keep-alive PONG desde ${source || 'unknown'}`)

				// Responder con PONG para mantener la conexión activa
				navigator.serviceWorker.controller?.postMessage({
					type: 'KEEP_ALIVE_PONG',
					timestamp: Date.now()
				})
			}
		}

		// Registrar el listener
		navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage)

		// Cleanup
		return () => {
			navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage)
		}
	}, [navigate])

	// Background Sync API - Sistema keep-alive mejorado
	useEffect(() => {
		const setupBackgroundSync = async () => {
			// Verificar si Service Worker está disponible
			if (!('serviceWorker' in navigator)) {
				console.warn('⚠️ [Layout] Service Worker no disponible en este navegador')
				return
			}

			try {
				const registration = await navigator.serviceWorker.ready

				// Verificar si Background Sync está disponible
				if (!registration.sync) {
					console.warn('⚠️ [Layout] Background Sync no disponible en este navegador')
					return
				}

				console.log('✅ [Layout] Background Sync disponible')

				// Registrar sync cada 25 segundos
				const syncInterval = setInterval(async () => {
					try {
						await registration.sync.register('storehub-keepalive')
						console.log('🔄 [Layout] Background sync registrado')
					} catch (error) {
						console.error('❌ [Layout] Error registrando Background Sync:', error)
					}
				}, 25000) // 25 segundos

				// Registrar uno inmediatamente
				try {
					await registration.sync.register('storehub-keepalive')
					console.log('🔄 [Layout] Background sync inicial registrado')
				} catch (error) {
					console.error('❌ [Layout] Error en sync inicial:', error)
				}

				// Cleanup
				return () => {
					clearInterval(syncInterval)
				}
			} catch (error) {
				console.error('❌ [Layout] Error configurando Background Sync:', error)
			}
		}

		setupBackgroundSync()
	}, [])

	useEffect(() => {
		fetchPublicParameters()
	}, [])

	// Efecto para cargar traducciones dinámicas
	useEffect(() => {
		const loadTranslations = async () => {
			if (signedIn) {
				try {
					const response = await apiGetParametersSettings()
					
					// Actualizar traducciones dinámicas
					await updateDynamicTranslations(response.data.data)
					
					// Marcar traducciones como cargadas
					setTranslationsLoaded(true)
				} catch (error) {
					console.error('Error loading parameters settings:', error)
					// En caso de error, permitir que la app continue
					setTranslationsLoaded(true)
				}
			} else {
				// Si no está logueado, marcar traducciones como "cargadas"
				setTranslationsLoaded(true)
			}
		}

		loadTranslations()
	}, [signedIn])

	const fetchData = async () => {
		dispatch(getTicketGlobalData())
	}

	const fetchPublicParameters = async () => {
		if (!signedIn) {
			const { data } = await apiGetPublicParameters()
			dispatch(setPublicParameters(data))
		}
	}

	const response = useQuery(PARAMETERS_TENANT, { skip: !signedIn })

	useEffect(() => {
		if (!response.loading && response.data) {
			dispatch(setParameters(response.data))
		}
	}, [response.loading, response.data, dispatch])

	const AppLayout = useMemo(() => {
		if (authenticated) {
			return layouts[layoutType]
		}
		return lazy(() => import('./AuthLayout'))
	}, [layoutType, authenticated])

	// No renderizar los componentes hasta que las traducciones estén cargadas
	if (!translationsLoaded) {
		return (
			<div className="flex flex-auto flex-col items-center justify-center h-[100vh]">
				<LogoSplash size={140} />
			</div>
		)
	}

	return (
		<Suspense
			fallback={
				<div className="flex flex-auto flex-col items-center justify-center h-[100vh]">
					<LogoSplash size={140} />
				</div>
			}
		>
			{/* NotificationManager es el ÚNICO que avisa: escucha los seis eventos
			    de Pusher, alimenta la campanita y decide si el aviso va al sistema
			    o al toast de adentro. Antes convivía con TicketNotification, que
			    escuchaba lo mismo y avisaba por su cuenta, así que cada evento
			    producía dos avisos. */}
			<NotificationManager>
				<AppLayout />
				<TicketDialog />
				{/* {process.env.NODE_ENV === 'development' && <PusherDebug />} */}
			</NotificationManager>
		</Suspense>
	)
}

export default memo(Layout)